import type { RouteObject } from 'react-router-dom';
import { lazyWithRetry } from '@repo/error';

import AccountLayout from '@features/account/layouts/AccountLayout';
import VehicleExistsGuard from '@features/account/guards/VehicleExistsGuard';
import BillingExistsGuard from '@features/account/guards/BillingExistsGuard';
import { AuthGuard, PermissionGuard } from '@features/auth';
import ReservationsProvider from '@features/account/providers/ReservationsProvider';
import ReservationExistsGuard from '@features/account/guards/ReservationExistsGuard';
import TicketExistsGuard from '@features/account/guards/TicketExistsGuard';
import RegistrationLayout from '@features/account/layouts/RegistrationLayout';

import DefaultLayout from '@shared/layouts/DefaultLayout';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

const AccountPage = lazyWithRetry(() => import('@features/account/pages/AccountPage'));
const AccountEditPage = lazyWithRetry(() => import('@features/account/pages/AccountEditPage'));
const VehiclesPage = lazyWithRetry(() => import('@features/account/pages/VehiclesPage'));
const VehicleDetailsPage = lazyWithRetry(() => import('@features/account/pages/VehicleDetailsPage'));
const VehicleCreatePage = lazyWithRetry(() => import('@features/account/pages/VehicleCreatePage'));
const BillingCreatePage = lazyWithRetry(() => import('@features/account/pages/BillingCreatePage'));
const BillingEditPage = lazyWithRetry(() => import('@features/account/pages/BillingEditPage'));
const BillingsPage = lazyWithRetry(() => import('@features/account/pages/BillingsPage'));
const ReservationsHistoryPage = lazyWithRetry(() => import('@features/account/pages/ReservationsHistoryPage'));
const ReservationPage = lazyWithRetry(() => import('@features/account/pages/ReservationPage'));
const ReservationsPage = lazyWithRetry(() => import('@features/account/pages/ReservationsPage'));
const ReservationPendingPage = lazyWithRetry(() => import('@features/account/pages/ReservationPendingPage'));
const PaymentHistoryPage = lazyWithRetry(() => import('@features/account/pages/PaymentHistoryPage'));
const WalletPage = lazyWithRetry(() => import('@features/account/pages/WalletPage'));
const WalletTransactionsPage = lazyWithRetry(() => import('@features/account/pages/WalletTransactionsPage'));
const WalletTopUpPage = lazyWithRetry(() => import('@features/account/pages/WalletTopUpPage'));
const WalletTopUpPaymentPage = lazyWithRetry(() => import('@features/account/pages/WalletTopUpPaymentPage'));
const ContactPage = lazyWithRetry(() => import('@features/account/pages/ContactPage'));
const RegulationsPage = lazyWithRetry(() => import('@features/account/pages/RegulationsPage'));
const TicketsPage = lazyWithRetry(() => import('@features/account/pages/TicketsPage'));
const TicketCreatePage = lazyWithRetry(() => import('@features/account/pages/TicketCreatePage'));
const TicketCreateFromReservationPage = lazyWithRetry(
  () => import('@features/account/pages/TicketCreateFromReservationPage')
);
const TicketSentPage = lazyWithRetry(() => import('@features/account/pages/TicketSentPage'));
const TicketDetailsPage = lazyWithRetry(() => import('@features/account/pages/TicketDetailsPage'));
const StartEarningPage = lazyWithRetry(() => import('@features/account/pages/StartEarningPage'));
const PartnerPage = lazyWithRetry(() => import('@features/account/pages/PartnerPage'));
const ControllerPage = lazyWithRetry(() => import('@features/account/pages/ControllerPage'));
const BecomeControllerPage = lazyWithRetry(() => import('@features/account/pages/BecomeControllerPage'));

export const accountRoutes: RouteObject[] = [
  {
    element: <DefaultLayout headerTheme="white" theme="soft-orange" />,
    children: [
      {
        element: <AccountLayout />,
        children: [
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT],
            element: <AccountPage />,
          },
        ],
      },
      {
        element: <RegistrationLayout />,
        children: [
          {
            path: ROUTE_PATHS[RouteName.START_EARNING],
            element: <StartEarningPage />,
          },
        ],
      },
      {
        element: <AuthGuard />,
        children: [
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_EDIT],
            element: <AccountEditPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_PARTNER],
            element: <PartnerPage />,
          },
          {
            element: <PermissionGuard permissions={['unauthorizedUseCase.create']} />,
            children: [
              {
                path: ROUTE_PATHS[RouteName.ACCOUNT_CONTROLLER],
                element: <ControllerPage />,
              },
            ],
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_BECOME_CONTROLLER],
            element: <BecomeControllerPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_VEHICLES],
            element: <VehiclesPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_VEHICLE_DETAILS],
            element: <VehicleExistsGuard />,
            children: [
              {
                index: true,
                element: <VehicleDetailsPage />,
              },
            ],
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_VEHICLE_CREATE],
            element: <VehicleCreatePage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_BILLINGS],
            element: <BillingsPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_BILLING_CREATE],
            element: <BillingCreatePage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_BILLING_EDIT],
            element: <BillingExistsGuard />,
            children: [
              {
                index: true,
                element: <BillingEditPage />,
              },
            ],
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS_HISTORY],
            element: <ReservationsHistoryPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_PAYMENT_HISTORY],
            element: <PaymentHistoryPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_WALLET],
            element: <WalletPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_WALLET_TRANSACTIONS],
            element: <WalletTransactionsPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_WALLET_TOP_UP],
            element: <WalletTopUpPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_WALLET_TOP_UP_PAYMENT],
            element: <WalletTopUpPaymentPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_TICKETS],
            element: <TicketsPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_TICKET_CREATE],
            element: <TicketCreatePage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_TICKET_CREATE_FROM_RESERVATION],
            element: <TicketCreateFromReservationPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_TICKET_SENT],
            element: <TicketSentPage />,
          },
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_TICKET_DETAILS],
            element: <TicketExistsGuard />,
            children: [
              {
                index: true,
                element: <TicketDetailsPage />,
              },
            ],
          },
        ],
      },
      {
        element: <AuthGuard allowGuest />,
        children: [
          {
            path: ROUTE_PATHS[RouteName.ACCOUNT_RESERVATION],
            element: <ReservationExistsGuard />,
            children: [
              {
                index: true,
                element: <ReservationPage />,
              },
            ],
          },
        ],
      },
      {
        path: ROUTE_PATHS[RouteName.ACCOUNT_CONTACT],
        element: <ContactPage />,
      },
      {
        path: ROUTE_PATHS[RouteName.ACCOUNT_REGULATIONS],
        element: <RegulationsPage />,
      },
    ],
  },
  {
    element: <DefaultLayout headerTheme="brand" theme="brand" />,
    children: [
      {
        element: <AuthGuard allowGuest />,
        children: [
          {
            element: <ReservationsProvider />,
            children: [
              {
                path: ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS],
                element: <ReservationsPage />,
              },
              {
                path: ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS_PENDING],
                element: <ReservationPendingPage />,
              },
            ],
          },
        ],
      },
    ],
  },
];
